import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

import type { BaseStorageService } from '@cosmos/data-access-storage';
import { collectRouteParams, collectRouteQueryParams } from '@cosmos/router';
import {
  CustomerPortalAuthActions,
  getCustomerPortalAuthDataFromLocalStorage,
} from '@customer-portal/auth/data-access-auth';

/**
 * Try to restore presentation session from storage.
 * @returns True if the presentation session is restored, False otherwise.
 */
export const tryToRestorePresentationSessionFromStorage = async (
  store: Store,
  storageService: BaseStorageService,
  route: ActivatedRouteSnapshot
) => {
  const presentationId = (collectRouteParams(route)?.['presentationId'] ??
    null) as string | null;
  if (presentationId) {
    const authData = getCustomerPortalAuthDataFromLocalStorage(
      storageService,
      presentationId
    );
    if (authData) {
      await firstValueFrom(
        store.dispatch(
          new CustomerPortalAuthActions.RestoreFromLocalStorage({
            session: authData.session,
            isUserDistributor: authData.isUserDistributor,
          })
        ),
        { defaultValue: undefined }
      );
      return true;
    }
  }
  return false;
};

export const handleLoginErrorRedirect = (error: unknown, router: Router) => {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 401 || error.status === 403) {
      // entity id is valid but access code is not
      // we should redirect user to unauthorized access page
      return router.navigate(['/unauthorized'], {
        skipLocationChange: true,
      });
    }
    if (error.status === 404) {
      // given entity id does not exist
      return router.navigate(['/not-found'], { skipLocationChange: true });
    }
  }
  // redirect to general error page
  return router.navigate(['/error'], { skipLocationChange: true });
};

export const getParams = (route: ActivatedRouteSnapshot) => {
  const { projectId, presentationId, documentId } = collectRouteParams(route);
  const { accessCode, token, postPayment } = collectRouteQueryParams(route);

  return {
    projectId: projectId ? parseInt(projectId, 10) : undefined,
    // Kept raw — a standalone presentation is addressed by uuid, which
    // `parseInt` would turn into `NaN`.
    presentationId: presentationId || undefined,
    documentId: documentId ? parseInt(documentId, 10) : undefined,
    accessCode: accessCode,
    token: postPayment ? undefined : token,
  };
};

const TOKENS_TO_STRIP = ['token', 'accessCode'];

/**
 * Checks if the given URL contains `token` or `accessCode` query parameters that should be stripped.
 */
export const checkIfUrlHasTokensToStrip = (
  router: Router,
  url: string
): boolean => {
  const urlTree = router.parseUrl(url);
  return TOKENS_TO_STRIP.some((token) => !!urlTree.queryParams[token]);
};

/**
 * Returns a new `UrlTree` with `token` and `accessCode` query parameters removed.
 * If they do not exist, the original `UrlTree` is returned.
 */
export const getUrlTreeWithoutTokens = (
  router: Router,
  url: string
): UrlTree => {
  const urlTree = router.parseUrl(url);
  for (const token of TOKENS_TO_STRIP) {
    delete urlTree.queryParams[token];
  }
  return urlTree;
};

/**
 * This function checks if the given URL contains `token` or `accessCode` query parameters.
 * If they exist, it removes them and returns a new `UrlTree`.
 * If they do not exist, it returns `true`.
 */
export const getUrlTreeWithoutTokensOrPass = (
  router: Router,
  url: string
): UrlTree | true => {
  if (checkIfUrlHasTokensToStrip(router, url)) {
    return getUrlTreeWithoutTokens(router, url);
  }
  return true;
};
